import React, { useState } from 'react';
import { 
  HelpCircle, 
  X, 
  ChevronUp, 
  ChevronDown, 
  BookOpen, 
  Lightbulb, 
  AlertCircle,
  CheckCircle,
  Clock,
  Users,
  BarChart3,
  Settings,
  MessageSquare,
  Database,
  Key,
  Edit3,
  Heart,
  Activity,
  TrendingUp,
  TrendingDown
} from 'lucide-react';

const ToolDocumentation = ({ toolName, isVisible, onClose, onToggle }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Documentation content for each tool
  const toolDocs = {
    'agent-feedback-dashboard': {
      title: 'Agent Feedback Dashboard',
      icon: <MessageSquare className="w-5 h-5" />,
      description: 'Monitor and analyze feedback for your AI agents to improve their performance.',
      sections: [
        {
          title: 'What it does',
          content: 'This dashboard allows you to view, filter, and analyze feedback logs for your registered AI agents. You can see how your agents are performing, identify areas for improvement, and track the effectiveness of your agent optimizations.'
        },
        {
          title: 'How to use it',
          content: [
            'Use the filters at the top to narrow down feedback by agent, tool, quality score, or main issue',
            'Click on any feedback entry to view detailed evaluation results',
            'Submit new feedback for evaluation using the form at the bottom',
            'Download CSV reports for external analysis'
          ]
        },
        {
          title: 'Key features',
          content: [
            'Real-time feedback monitoring',
            'Quality score tracking',
            'Agent-specific performance analysis',
            'Export capabilities for reporting',
            'Detailed evaluation insights'
          ]
        },
        {
          title: 'Pro tips',
          content: [
            'Regularly review low-quality scores to identify patterns',
            'Use the agent dropdown to focus on specific agents',
            'Export data periodically for trend analysis',
            'Submit feedback after significant agent changes'
          ]
        }
      ]
    },
    'customer-analytics-dashboard': {
      title: 'Customer Analytics Dashboard',
      icon: <BarChart3 className="w-5 h-5" />,
      description: 'Track customer usage patterns and agent interactions across your customer base.',
      sections: [
        {
          title: 'What it does',
          content: 'This dashboard provides insights into how your customers are using your AI agents. You can analyze usage patterns, identify popular features, and understand customer behavior to optimize your agent offerings.'
        },
        {
          title: 'How to use it',
          content: [
            'Set date ranges to analyze specific time periods',
            'Filter by tool and use case to focus on specific features',
            'Review usage statistics and trends',
            'Export data for external analysis'
          ]
        },
        {
          title: 'Key features',
          content: [
            'Customer usage analytics',
            'Tool and feature popularity tracking',
            'Usage trend analysis',
            'Customer behavior insights',
            'Export capabilities'
          ]
        },
        {
          title: 'Pro tips',
          content: [
            'Monitor usage spikes to identify successful features',
            'Track customer adoption of new agent features',
            'Use data to prioritize agent improvements',
            'Export monthly reports for stakeholder updates'
          ]
        }
      ]
    },
    'agent-analytics-dashboard': {
      title: 'Agent Performance Dashboard',
      icon: <BarChart3 className="w-5 h-5" />,
      description: 'Track detailed performance metrics, insights, and actionable recommendations for your AI agents.',
      sections: [
        {
          title: 'What it does',
          content: 'This dashboard provides comprehensive analytics on your AI agents\' performance, including response quality, usage patterns, improvement trends, and AI-generated insights. It helps you understand how well your agents are performing and provides actionable recommendations for optimization.'
        },
        {
          title: 'How to use it',
          content: [
            'Select specific agents to analyze their performance',
            'Set date ranges to focus on specific time periods',
            'Filter by tools and use cases',
            'Click "Get Insights" to view AI-generated recommendations',
            'Review performance metrics, trends, and suggested actions',
            'Export analytics data for reporting'
          ]
        },
        {
          title: 'Key features',
          content: [
            'Agent-specific performance tracking',
            'Quality score analytics with trend analysis',
            'AI-generated insights and recommendations',
            'Recent issues identification and tracking',
            'Performance trend visualization (Improving/Steady/Declining)',
            'Actionable improvement suggestions',
            'Comparative agent analysis'
          ]
        },
        {
          title: 'Pro tips',
          content: [
            'Use the "Get Insights" feature to get AI-powered recommendations',
            'Focus on agents with declining performance scores',
            'Review recent issues to identify common problems',
            'Follow suggested actions to improve agent performance',
            'Track performance before and after implementing recommendations',
            'Use trends to predict when agents need updates'
          ]
        }
      ]
    },
    'customer-manager': {
      title: 'Customer Manager',
      icon: <Users className="w-5 h-5" />,
      description: 'Manage customer accounts, API keys, and usage limits (Admin only).',
      sections: [
        {
          title: 'What it does',
          content: 'This tool allows administrators to manage customer accounts, including creating new customers, setting usage limits, configuring API keys, and monitoring customer activity. It provides full control over customer access and usage.'
        },
        {
          title: 'How to use it',
          content: [
            'Create new customer accounts with appropriate limits',
            'Manage API keys and access permissions',
            'Monitor customer usage and activity',
            'Set and adjust usage quotas',
            'Configure webhook URLs for notifications'
          ]
        },
        {
          title: 'Key features',
          content: [
            'Customer account management',
            'API key generation and management',
            'Usage limit configuration',
            'Customer activity monitoring',
            'Webhook configuration'
          ]
        },
        {
          title: 'Pro tips',
          content: [
            'Set realistic usage limits based on customer needs',
            'Monitor usage patterns to identify potential issues',
            'Use webhooks to stay informed of customer activity',
            'Regularly review and update customer permissions'
          ]
        }
      ]
    },
    'agent-prompt-editor': {
      title: 'Edit Agent Prompts',
      icon: <Edit3 className="w-5 h-5" />,
      description: 'Modify and optimize the system prompts for your AI agents.',
      sections: [
        {
          title: 'What it does',
          content: 'This tool allows you to view and edit the system prompts for your registered AI agents. System prompts define how your agents behave and respond, so optimizing them can significantly improve agent performance.'
        },
        {
          title: 'How to use it',
          content: [
            'Select an agent from the dropdown menu',
            'Review the current system prompt',
            'Make edits to improve agent behavior',
            'Save changes to update the agent',
            'Test the agent after making changes'
          ]
        },
        {
          title: 'Key features',
          content: [
            'Real-time prompt editing',
            'Agent selection interface',
            'Change tracking and history',
            'Save and revert functionality',
            'Last updated timestamps'
          ]
        },
        {
          title: 'Pro tips',
          content: [
            'Make small, incremental changes to track improvements',
            'Test agents after prompt changes',
            'Use clear, specific instructions in prompts',
            'Consider the agent\'s role when writing prompts',
            'Back up important prompts before major changes'
          ]
        }
      ]
    },
    'agent-manager': {
      title: 'Manage My Agents',
      icon: <Settings className="w-5 h-5" />,
      description: 'Register, edit, and manage your AI agents.',
      sections: [
        {
          title: 'What it does',
          content: 'This tool allows you to register new AI agents, edit existing agent details, and manage your agent portfolio. You can create agents for different purposes, configure their settings, and organize your AI workforce.'
        },
        {
          title: 'How to use it',
          content: [
            'Register new agents with specific types and descriptions',
            'Edit agent names, descriptions, and configurations',
            'Delete agents you no longer need',
            'Search and filter your agent list',
            'View agent details and creation dates'
          ]
        },
        {
          title: 'Key features',
          content: [
            'Agent registration and management',
            'Agent type configuration',
            'LLM model selection',
            'Agent editing and deletion',
            'Search and filter capabilities'
          ]
        },
        {
          title: 'Pro tips',
          content: [
            'Use descriptive names for easy identification',
            'Choose appropriate agent types for your use cases',
            'Regularly review and clean up unused agents',
            'Test new agents before deploying them widely',
            'Document agent purposes in descriptions'
          ]
        }
      ]
    },
    'agent-self-healing': {
      title: 'Agent Self-Healing',
      icon: <Heart className="w-5 h-5" />,
      description: 'Monitor and automatically heal underperforming AI agents with intelligent prompt optimization.',
      sections: [
        {
          title: 'What it does',
          content: 'This tool monitors your AI agents for performance issues and automatically suggests prompt improvements to "heal" underperforming agents. It uses a two-step process: first scans to flag agents needing healing, then applies improvements only to flagged agents (prevents 400 errors).'
        },
        {
          title: 'How to use it',
          content: [
            'Run a healing scan to identify agents needing attention',
            'Review flagged agents and their performance issues',
            'View detailed analysis of agent problems and trends',
            'Apply suggested prompt updates to heal agents',
            'Monitor healing status and track improvements',
            'Review healing history and applied changes',
            'With SDK: Automatic two-step process prevents 400 errors'
          ]
        },
        {
          title: 'Key features',
          content: [
            'Automated agent performance monitoring',
            'Intelligent issue detection and analysis',
            'AI-powered prompt optimization suggestions',
            'Performance trend analysis (Improving/Steady/Declining)',
            'Individual agent healing controls',
            'Healing history and change tracking',
            'Real-time agent status monitoring',
            'Two-step process prevents 400 errors',
            'Automatic agent flagging and validation'
          ]
        },
        {
          title: 'Pro tips',
          content: [
            'Run regular healing scans to catch issues early',
            'Review the analysis before applying prompt changes',
            'Monitor agents after healing to ensure improvements',
            'Use the trend analysis to predict when agents need attention',
            'Keep track of successful healing strategies for future reference',
            'Combine with the Performance Dashboard for comprehensive agent management',
            'Use the drop-in SDK for automatic error prevention',
            'The SDK handles the two-step process automatically'
          ]
        }
      ]
    },
    'api-keys': {
      title: 'My API Keys',
      icon: <Key className="w-5 h-5" />,
      description: 'Manage your API keys for accessing agent services.',
      sections: [
        {
          title: 'What it does',
          content: 'This tool allows you to view and manage your API keys used for accessing agent services. API keys are required for agent registration and other agent-related operations.'
        },
        {
          title: 'How to use it',
          content: [
            'View your current API key',
            'Copy the key for use in applications',
            'Understand key permissions and usage',
            'Contact admin for key changes if needed'
          ]
        },
        {
          title: 'Key features',
          content: [
            'API key display and management',
            'Secure key handling',
            'Usage information',
            'Key status monitoring'
          ]
        },
        {
          title: 'Pro tips',
          content: [
            'Keep your API key secure and don\'t share it',
            'Use the key in your agent registration requests',
            'Contact support if you need a new key',
            'Monitor key usage to ensure proper access'
          ]
        }
      ]
    }
  };

  const currentTool = toolDocs[toolName];

  if (!currentTool || !isVisible) {
    return null;
  }

  return (
    <div className="fixed right-4 top-20 w-80 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-700">
        <div className="flex items-center space-x-2">
          <div className="text-purple-400">
            {currentTool.icon}
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm">{currentTool.title}</h3>
            <p className="text-gray-400 text-xs">{currentTool.description}</p>
          </div>
        </div>
        <div className="flex items-center space-x-1">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 text-gray-400 hover:text-white transition-colors"
          >
            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-red-400 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 max-h-96 overflow-y-auto">
          {currentTool.sections.map((section, index) => (
            <div key={index} className="mb-4 last:mb-0">
              <h4 className="text-white font-medium text-sm mb-2 flex items-center">
                {section.title === 'What it does' && <BookOpen className="w-4 h-4 mr-2 text-blue-400" />}
                {section.title === 'How to use it' && <CheckCircle className="w-4 h-4 mr-2 text-green-400" />}
                {section.title === 'Key features' && <BarChart3 className="w-4 h-4 mr-2 text-purple-400" />}
                {section.title === 'Pro tips' && <Lightbulb className="w-4 h-4 mr-2 text-yellow-400" />}
                {section.title}
              </h4>
              <div className="text-gray-300 text-xs leading-relaxed">
                {Array.isArray(section.content) ? (
                  <ul className="space-y-1">
                    {section.content.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start">
                        <span className="text-purple-400 mr-2">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>{section.content}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ToolDocumentation; 